import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router';
import { CircleAlert, CircleCheck, Compass, Info, LifeBuoy, Server, Shield, Users, WifiOff, X } from 'lucide-react';
import { Avatar, clsx, Spinner } from '../../components/ui';
import { desktop, useDesktopIntegration } from '../../lib/desktop';
import { useAppRoute, useIsMobile } from '../../lib/hooks';
import { lastChannel } from '../../lib/local';
import { channelsOfSpace, dmPartner, privateChannelName, useStore } from '../../store/store';
import { useUi, ui } from '../../store/ui';
import { AdminSidebar, AdminView } from '../admin/AdminView';
import { ChannelView } from '../chat/ChannelView';
import { Dialogs } from '../dialogs/Dialogs';
import { DiscoverSidebar, DiscoverView } from '../discover/DiscoverView';
import { FriendsView } from '../friends/FriendsView';
import { ServersView } from '../servers/ServersView';
import { StandingNotice } from '../standing/Standing';
import { SupportView } from '../support/SupportView';
import { IncomingCall } from '../voice/IncomingCall';
import { HomeSidebar } from './HomeSidebar';
import { PushPrompt } from './PushPrompt';
import { useGlobalShortcuts } from './shortcuts';
import { SpaceIcon } from './SpaceIcon';
import { SpaceRail } from './SpaceRail';
import { SpaceSidebar } from './SpaceSidebar';
import { useMobileSwipe } from './useMobileSwipe';
import { UserPanel } from './UserPanel';
import { VoicePanel } from './VoicePanel';

function ConnectionBanner() {
  const connection = useStore((s) => s.connection);
  const hasData = useStore((s) => !!s.sessionId);
  if (connection === 'ready') return null;
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;
  return (
    <div role="status" className={clsx('flex items-center justify-center gap-2 px-3 py-1 text-xs font-medium', offline ? 'bg-danger text-white' : 'bg-warning text-black')}>
      {offline ? <WifiOff className="size-3.5" aria-hidden /> : <Spinner className="size-3.5 text-black" label="Reconnecting" />}
      {offline ? 'You are offline. Messages will sync when you reconnect.' : hasData ? 'Connection lost. Reconnecting…' : 'Connecting…'}
    </div>
  );
}

function Toasts() {
  const toasts = useUi((s) => s.toasts);
  return (
    <div className="pointer-events-none fixed bottom-4 left-1/2 z-[90] flex w-[calc(100vw-32px)] max-w-md -translate-x-1/2 flex-col gap-2" aria-live="polite">
      {toasts.map((t) => {
        const Icon = t.kind === 'error' ? CircleAlert : t.kind === 'success' ? CircleCheck : Info;
        return (
          <div key={t.id} className="animate-pop pointer-events-auto flex items-start gap-3 rounded-lg border border-line bg-overlay px-4 py-3 text-sm text-fg shadow-pop">
            <Icon className={clsx('mt-0.5 size-4 shrink-0', t.kind === 'error' ? 'text-danger' : t.kind === 'success' ? 'text-success' : 'text-accent')} aria-hidden />
            <span className="flex-1">{t.text}</span>
            <button type="button" aria-label="Dismiss" className="text-muted hover:text-fg" onClick={() => ui.dismissToast(t.id)}>
              <X className="size-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

/** Window-wide title strip naming where you are: Friends, a conversation, or the open space. Also sets the tab title. */
function TitleBar({ hidden }: { hidden?: boolean }) {
  const { spaceId, channelId, servers, support, admin, discover } = useAppRoute();
  const space =useStore((s) => (spaceId ? s.spaces[spaceId] : undefined));
  const channel = useStore((s) => (channelId ? s.channels[channelId] : undefined));
  useStore((s) => s.users);

  let icon: ReactNode;
  let title: string;
  if (admin) {
    icon = <Shield className="size-4 text-danger" aria-hidden />;
    title = 'Admin panel';
  } else if (discover) {
    icon = <Compass className="size-4 text-accent" aria-hidden />;
    title = 'Discover';
  } else if (servers) {
    icon = <Server className="size-4 text-muted" aria-hidden />;
    title = 'Game Servers';
  } else if (support) {
    icon = <LifeBuoy className="size-4 text-muted" aria-hidden />;
    title = 'Support';
  } else if (space) {
    icon = <SpaceIcon name={space.name} iconUrl={space.iconUrl} size={18} active />;
    title = space.name;
  } else if (channel && !channel.spaceId) {
    const state = useStore.getState();
    const partner = channel.type === 'dm' ? dmPartner(state, channel) : null;
    icon = partner ? <Avatar user={partner} size={18} /> : <Users className="size-4 text-muted" aria-hidden />;
    title = privateChannelName(state, channel);
  } else {
    icon = <Users className="size-4 text-muted" aria-hidden />;
    title = 'Friends';
  }

  useEffect(() => {
    document.title = `${title} | Spawnwire`;
  }, [title]);

  // Phones skip the strip to save space but still get the tab title. The Windows app shows the title in its own title bar.
  if (hidden) return null;
  return (
    <div className="flex h-9 shrink-0 items-center justify-center gap-2 bg-rail px-4 text-sm font-medium text-fg">
      {icon}
      <span className="truncate">{title}</span>
    </div>
  );
}

/** /app/:spaceId without a channel: go to the last visited or first text channel. */
function SpaceIndex() {
  const { spaceId = '' } = useParams();
  const space = useStore((s) => s.spaces[spaceId]);
  const channels = useStore((s) => s.channels);
  const ready = useStore((s) => s.connection === 'ready' || !!s.sessionId);
  if (!ready) return <div className="flex flex-1 items-center justify-center"><Spinner className="size-8" /></div>;
  if (!space) return <Navigate to="/app/@me" replace />;
  const last = lastChannel.get(spaceId);
  if (last && channels[last]?.spaceId === spaceId) return <Navigate to={`/app/${spaceId}/${last}`} replace />;
  const first = channelsOfSpace({ channels }, spaceId).find((c) => c.type === 'text' || c.type === 'announcement');
  if (first) return <Navigate to={`/app/${spaceId}/${first.id}`} replace />;
  return <div className="flex flex-1 items-center justify-center text-sm text-muted">This space has no channels you can see yet.</div>;
}

function SpaceChannel() {
  const { spaceId = '', channelId = '' } = useParams();
  const channel = useStore((s) => s.channels[channelId]);
  const ready = useStore((s) => !!s.sessionId);
  useEffect(() => {
    if (channel && channel.spaceId === spaceId) lastChannel.set(spaceId, channelId);
  }, [channel, spaceId, channelId]);
  if (!ready) return <div className="flex flex-1 items-center justify-center"><Spinner className="size-8" /></div>;
  if (!channel || channel.spaceId !== spaceId) return <Navigate to={`/app/${spaceId}`} replace />;
  return <ChannelView key={channelId} channelId={channelId} />;
}

function DmChannel() {
  const { channelId = '' } = useParams();
  const channel = useStore((s) => s.channels[channelId]);
  const ready = useStore((s) => !!s.sessionId);
  if (!ready) return <div className="flex flex-1 items-center justify-center"><Spinner className="size-8" /></div>;
  if (!channel || channel.spaceId) return <Navigate to="/app/@me" replace />;
  return <ChannelView key={channelId} channelId={channelId} />;
}

export function AppShell() {
  const { spaceId, channelId, servers, support, admin, discover } = useAppRoute();
  const isStaff = useStore((s) => !!s.me?.staffRole);
  const isMobile = useIsMobile();
  const mobilePane = useUi((s) => s.mobilePane);
  const spaceExists = useStore((s) => (spaceId ? !!s.spaces[spaceId] : false));
  useGlobalShortcuts();
  useDesktopIntegration();

  const stage = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  useMobileSwipe(stage, mainRef, isMobile);

  // On phones, signing in (or opening the app at home) starts on the conversation list, not the friends page.
  // Links straight to a channel or conversation still open that channel. A layout effect, so the conversation
  // doesn't visibly slide away on load.
  useLayoutEffect(() => {
    if (isMobile && !spaceId && !channelId && !servers && !support) ui.setMobilePane('nav');
    // Only on first load of the shell.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phones stack the conversation on top of the channel list and slide it aside to show the list.
  const navOpen = isMobile && mobilePane === 'nav';

  return (
    <div className="flex h-full flex-col bg-rail">
      <ConnectionBanner />
      <TitleBar hidden={isMobile || !!desktop} />
      <div ref={stage} className={clsx('flex min-h-0 flex-1', isMobile && 'relative overflow-hidden')}>
        <div inert={isMobile && !navOpen} className={clsx('flex shrink-0 flex-col', isMobile && 'absolute inset-0')}>
          <div className="flex min-h-0 flex-1">
            <SpaceRail />
            {/* The sidebar and main area read as one panel tucked under the title bar, with a rounded corner against the rail. */}
            <aside
              className={clsx(
                'flex min-h-0 flex-col overflow-hidden border-l border-t border-line bg-sidebar',
                isMobile ? 'mt-2 min-w-0 flex-1 rounded-tl-3xl' : 'w-72 rounded-tl-xl',
              )}
            >
              {admin && isStaff ? <AdminSidebar /> : discover ? <DiscoverSidebar /> : spaceId && spaceExists ? <SpaceSidebar spaceId={spaceId} /> : <HomeSidebar />}
            </aside>
          </div>
          {/* The account card floats across the rail and the sidebar, like the rail's own column continuing beneath it. */}
          <div className="shrink-0 bg-[linear-gradient(to_right,var(--p-rail)_72px,var(--p-sidebar)_72px)] p-2 pt-0">
            <div className={clsx('overflow-hidden border border-line bg-elevated shadow-pop', isMobile ? 'rounded-2xl' : 'rounded-lg')}>
              <VoicePanel />
              <UserPanel />
            </div>
          </div>
        </div>
        <main
          ref={mainRef}
          inert={navOpen}
          className={clsx(
            'flex min-w-0 flex-1 flex-col bg-main',
            isMobile ? clsx('absolute inset-0 z-10 shadow-pop transition-transform duration-200 ease-out', navOpen ? 'translate-x-full' : 'translate-x-0') : 'border-t border-line',
          )}
        >
          <Routes>
            <Route path="@me" element={<FriendsView />} />
            <Route path="@me/:channelId" element={<DmChannel />} />
            <Route path="servers/*" element={<ServersView />} />
            <Route path="support/*" element={<SupportView />} />
            <Route path="admin/*" element={<AdminView />} />
            <Route path="discover" element={<DiscoverView />} />
            <Route path="minecraft" element={<Navigate to="/app/servers" replace />} />
            <Route path=":spaceId" element={<SpaceIndex />} />
            <Route path=":spaceId/:channelId" element={<SpaceChannel />} />
            <Route path="*" element={<Navigate to="/app/@me" replace />} />
          </Routes>
        </main>
      </div>
      <Dialogs />
      <IncomingCall />
      <StandingNotice />
      <PushPrompt />
      <Toasts />
    </div>
  );
}
